import { type FC } from "react";

import { Card, CardRowDetail, CardRowDetailColumn, CardRowDetailLine, CardRowDetailLineSpacer } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { type Range } from "../../../graphql.js";
import { Nucleotide, Nucleotides } from "../../blast-result/ColoredSequence.js";
import { SelectRangeButton } from "../SelectRangeButton.js";
import { type INgsReadsTooltip } from "./NgsReadsTrackLoader.js";
import { CIGAR_REGEXP } from "./_mod.js";

// @see https://broadinstitute.github.io/picard/explain-flags.html
const FLAGS_EXPLANATION = [
  "Read paired", // 0x1 = 1, binIdx = 0
  "Read mapped in proper pair", // 0x2 = 2, binIdx = 1
  "Read unmapped", // 0x4 = 4, binIdx = 2
  "Mate unmapped", // 0x8 = 8, binIdx = 3
  "Read reverse strand", // 0x10 = 16, binIdx = 4
  "Mate reverse strand", // 0x20 = 32, binIdx = 5
  "First in pair", // 0x40 = 64, binIdx = 6
  "Second in pair", // 0x80 = 128, binIdx = 7
  "Not primary alignment", // 0x100 = 256, binIdx = 8
  "Read fails platform/vendor quality checks", // 0x200 = 512, binIdx = 9
  "Read is PCR or optical duplicate", // 0x400 = 1024, binIdx = 10
  "Supplementary alignment", // 0x800 = 2048, binIdx = 11
];

// int to binary array, like [1, 0, 0, 1, ..]
const flagsIntToBinaryArray = (flags: number) => [...Array(12)].map((_x, i) => (flags >> i) & 1);

const NgsReadsTrackTooltip: FC<INgsReadsTooltip> = (data) => {
  const distance = data.segment.start - data.alignment.start;
  const { deletion, insertion } = data.segment;
  const { cigar } = data.alignment;
  let delInsIndex = -1; // first is zero (it's an array)
  let traveledDistance = 0;

  if (cigar) {
    // parse cigar
    const cigarIterator = cigar.matchAll(CIGAR_REGEXP);

    for (const cigarState of cigarIterator) {
      const length = Number(cigarState[1]);
      const operationCode = cigarState[2];

      if (traveledDistance > distance) {
        break;
      }

      if (insertion && operationCode === "I") {
        delInsIndex += 1;
        // Insertions are not drawn
      }

      if (deletion && operationCode === "D") {
        delInsIndex += 1;
        traveledDistance += length;
      }

      // Don't take soft clipping into account => operationCode === "S"
      if (operationCode === "M" || operationCode === "X" || operationCode === "=" || operationCode === "EQ") {
        traveledDistance += length;
      }
    }
  }

  return (
    <Card
      title={data?.alignment?.referenceName}
      subtitle={"Read"}
      actions={[<SelectRangeButton range={{ start: data.segment.start + 1, stop: data.segment.stop } as Range} />]}
      noBorder
    >
      <CardRowDetail>
        <CardRowDetailColumn>
          <CardRowDetailLine title="Reference name">{data?.alignment?.referenceName}</CardRowDetailLine>
          {data?.alignment?.readGroup && (
            <CardRowDetailLine title="Read group">{data?.alignment?.readGroup}</CardRowDetailLine>
          )}
          <CardRowDetailLine title="Reference region">
            {formatNumberByThousands(data?.alignment?.start + 1)} — {formatNumberByThousands(data?.alignment?.end)}
          </CardRowDetailLine>
          <CardRowDetailLine title="Alignment length">
            {formatNumberByThousands(data?.alignment?.readLength)}
          </CardRowDetailLine>
          <CardRowDetailLine title="Strand">{data?.alignment?.strand}</CardRowDetailLine>
          <CardRowDetailLineSpacer />
          <CardRowDetailLine title="CIGAR" wrap>
            <CigarString cigar={data?.alignment?.cigar} />
          </CardRowDetailLine>
          <CardRowDetailLineSpacer />
          <CardRowDetailLine title="Mapping quality">{data?.alignment?.mappingQuality}</CardRowDetailLine>
          <CardRowDetailLine title="Avg. base quality">{data?.alignment?.averageBaseQuality}</CardRowDetailLine>
          <CardRowDetailLineSpacer />
          <CardRowDetailLine title="Mismatches">{data?.alignment?.mismatches}</CardRowDetailLine>
          <CardRowDetailLine title="Deletions">{data?.alignment?.deletions?.join("").length}</CardRowDetailLine>
          <CardRowDetailLine title="Insertions">{data?.alignment?.insertions?.join("").length}</CardRowDetailLine>
          {data?.alignment?.mateMapped && (
            <>
              <CardRowDetailLineSpacer />
              <CardRowDetailLine title="Mate ref. name">{data?.alignment?.mateReferenceName}</CardRowDetailLine>
              <CardRowDetailLine title="Mate start">
                {formatNumberByThousands(data?.alignment?.mateStart + 1)}
              </CardRowDetailLine>
              <CardRowDetailLine title="Mate strand">{data?.alignment?.mateStrand}</CardRowDetailLine>
              <CardRowDetailLine title="Mate mapped">{String(data?.alignment?.mateMapped)}</CardRowDetailLine>
              <CardRowDetailLine title="Insert size">
                {formatNumberByThousands(data?.alignment?.insertSize)}
              </CardRowDetailLine>
            </>
          )}
        </CardRowDetailColumn>
        <CardRowDetailColumn>
          {data?.alignment?.flags != undefined && (
            <CardRowDetailLine title="SAM flags">
              {flagsIntToBinaryArray(data?.alignment?.flags)
                .map((x, i) => (x === 1 ? FLAGS_EXPLANATION[i] : null)) // map to explanations
                .filter((v) => v) // remove empty
                .map((v) => {
                  return (
                    <>
                      {v}
                      <br />
                    </>
                  );
                })}
            </CardRowDetailLine>
          )}
          <CardRowDetailLineSpacer />
          <CardRowDetailLine title="GC content">{data?.alignment?.percentGC}%</CardRowDetailLine>
          <CardRowDetailLineSpacer />
          <CardRowDetailLine title="Attributes" wrap>
            {data?.alignment?.attributes?.map((v, i) => (
              <span style={{ wordBreak: "break-all" }} key={`ngs-reads-track-tooltip-attribute-${i}`}>
                {v}
                <br />
              </span>
            ))}
          </CardRowDetailLine>
        </CardRowDetailColumn>
      </CardRowDetail>
      {data?.segment?.match && data?.index != null && (
        <CardRowDetail title={"Mismatch"} style={{ fontSize: 12, lineHeight: 1.15 }}>
          <CardRowDetailColumn>
            <CardRowDetailLine title="Nucleotide">
              <Nucleotide letter={data.segment.mismatchesBases[data.index]} />
            </CardRowDetailLine>
            <CardRowDetailLine title="Position">
              {formatNumberByThousands(data.segment.start + data.segment.mismatchPositions[data.index] + 1)}
            </CardRowDetailLine>
            <CardRowDetailLine title="Quality">{data.segment.mismatchBasesQualities[data.index]}</CardRowDetailLine>
          </CardRowDetailColumn>
        </CardRowDetail>
      )}
      {data?.segment?.match && data?.index == null && (
        <CardRowDetail title={"Alignment block"} style={{ fontSize: 12, lineHeight: 1.15 }}>
          <CardRowDetailColumn>
            <CardRowDetailLine title="Read segment">
              {formatNumberByThousands(data.segment.start + 1)} — {formatNumberByThousands(data.segment.stop)}
            </CardRowDetailLine>
            <CardRowDetailLine title="Length">
              {formatNumberByThousands(data.segment.stop - data.segment.start)}
            </CardRowDetailLine>
            <CardRowDetailLine title="Mismatches">{data.segment.mismatchesBases?.length}</CardRowDetailLine>
          </CardRowDetailColumn>
        </CardRowDetail>
      )}
      {data?.segment?.deletion && (
        <CardRowDetail title={"Deletions"} style={{ fontSize: 12, lineHeight: 1.15 }}>
          <CardRowDetailColumn>
            <CardRowDetailLine title="Read segment">
              {formatNumberByThousands(data.segment.start + 1)} — {formatNumberByThousands(data.segment.stop)}
            </CardRowDetailLine>
            <CardRowDetailLine title="Length">
              {formatNumberByThousands(data.segment.stop - data.segment.start)}
            </CardRowDetailLine>
            <CardRowDetailLine title="Nucleotides" wrap>
              <Nucleotides seq={data.alignment?.deletions?.[delInsIndex]} />
            </CardRowDetailLine>
          </CardRowDetailColumn>
        </CardRowDetail>
      )}
      {data?.segment?.insertion && (
        <CardRowDetail title={"Insertions"} style={{ fontSize: 12, lineHeight: 1.15 }}>
          <CardRowDetailColumn>
            <CardRowDetailLine title="Position">{formatNumberByThousands(data.segment.start)}</CardRowDetailLine>
            <CardRowDetailLine title="Length">{[...data.alignment.insertions?.[delInsIndex]].length}</CardRowDetailLine>
            <CardRowDetailLine title="Nucleotides" wrap>
              <Nucleotides seq={data.alignment.insertions?.[delInsIndex]} />
            </CardRowDetailLine>
          </CardRowDetailColumn>
        </CardRowDetail>
      )}
    </Card>
  );
};

// Beautify CIGAR string
const CigarString: FC<{ cigar: string }> = ({ cigar }) => {
  if (cigar == undefined) return null;

  const cigarIterator = cigar.matchAll(CIGAR_REGEXP);
  const parts: Array<[JSX.Element, JSX.Element]> = [];

  for (const cigarState of cigarIterator) {
    const num = cigarState[1];
    const ope = cigarState[2];
    parts.push([
      <span style={{ color: "#244671" }} key={`cigar-string-${ope}-num`}>
        {num}
      </span>,
      <span key={`cigar-string-${ope}`}>{ope}</span>,
    ]);
  }

  // TODO draw heatmap if too long
  return (
    <div
      style={{
        fontFamily: "Roboto Flex Mono, monospace",
        // maxWidth: 240,
        // overflow: "hidden",
        // textOverflow: "ellipsis",
        wordWrap: "break-word",
      }}
    >
      {parts.slice(0, 10).map((p, i) => (
        <span style={{ paddingRight: 2 }} key={`cigar-string-${i}`}>
          {p}
        </span>
      ))}
      {parts.length > 10 ? "..." : null}
    </div>
  );
};

export { CardRowDetailLine, CardRowDetailLineSpacer, NgsReadsTrackTooltip, flagsIntToBinaryArray };
